/* Bulk policy agreement. Legacy protection fees are removed from saved carts. */
(() => {
  if (customElements.get('pj-bulk-cart')) return;
  // Craftly's checkout validator is pinned to this policy key. Keep accepting the
  // short-lived delivery-promise key so already-accepted carts migrate in place.
  const VERSION = 'bulk-2026-09-06-v1';
  const COMPATIBLE_VERSIONS = new Set([VERSION, 'bulk-delivery-promise-2026-09-16-v1']);
  const DELIVERY_PROMISE_VERSION = 'bulk-delivery-promise-2026-09-16-v1';
  const root = window.Shopify?.routes?.root || '/';
  const panels = () => [...document.querySelectorAll('pj-bulk-cart')];
  let state = {agreed: false, fingerprint: '', acceptedAt: ''};
  let busy = false, externalUpdate = false, ready = false, error = '';
  let resolutionReady = false;
  let queue = Promise.resolve();
  const isLegacyFee = item => item.product_type === 'Bulk Theft Protection' || /^PJ-BTP-\d+$/.test(item.sku || '');

  async function request(path, data) {
    const response = await fetch(root + path, {method: data ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
      headers: {'Accept': 'application/json', ...(data ? {'Content-Type': 'application/json'} : {})},
      ...(data ? {body: JSON.stringify(data)} : {})});
    const body = await response.json();
    if (!response.ok) throw new Error(body.description || 'Your cart could not be updated. Please try again.');
    return body;
  }

  const readCart = () => request('cart.js');
  const fingerprint = () => panels()[0]?.dataset.fingerprint || '';
  function sectionIds() {
    return [...new Set(['cart-button', ...[...document.querySelectorAll('cart-section,cart-drawer')]
      .map(el => el.closest('.shopify-section')?.id.replace(/^shopify-section-/, '')).filter(Boolean)])].slice(0, 5);
  }
  async function refresh() {
    const response = await request('cart/update.js', {sections: sectionIds(), sections_url: location.pathname + location.search});
    document.dispatchEvent(new CustomEvent('cart:updated', {detail: {type: 'update', request: {}, response, pjBulkPolicy: true}}));
    return response;
  }
  function render() {
    const active = panels();
    document.documentElement.classList.toggle('pj-bulk-cart-present', active.length > 0);
    for (const panel of active) {
      const agree = panel.querySelector('[data-bulk-agree]');
      agree.checked = state.agreed;
      agree.disabled = busy || externalUpdate || !ready;
      panel.querySelector('[data-bulk-status]').textContent = error || (busy || externalUpdate || !ready
        ? 'Updating your bulk order…' : !resolutionReady
          ? 'Choose an availability preference for every Bulk item before checkout.' : state.agreed
          ? 'Bulk & Wholesale Purchase Policy accepted.'
          : 'Please agree to the bulk policy before checkout.');
    }
    for (const button of document.querySelectorAll('button[name="checkout"]')) {
      if (active.length) {
        button.dataset.pjBulkControlled = 'true';
        button.disabled = !ready || busy || externalUpdate || !resolutionReady || !state.agreed || Boolean(error);
      } else if (button.dataset.pjBulkControlled) {
        button.disabled = busy || externalUpdate || Boolean(error);
        if (!busy && !externalUpdate && !error) delete button.dataset.pjBulkControlled;
      }
    }
  }
  async function sync(action) {
    busy = true; error = ''; render();
    try {
      let cart = await readCart();
      const legacyFees = cart.items.filter(isLegacyFee);
      const removedLegacyFees = legacyFees.length > 0;
      if (legacyFees.length) {
        await request('cart/update.js', {updates: Object.fromEntries(legacyFees.map(item => [item.key, 0]))});
        cart = await readCart();
      }

      const currentFingerprint = fingerprint();
      const attributes = cart.attributes || {};
      const hasManagedAttributes = [
        'pj_bulk_policy_version',
        'pj_bulk_policy_accepted_at',
        'pj_bulk_fingerprint',
        'pj_bulk_delivery_promise_version',
        'pj_bulk_protection',
        'pj_bulk_protection_fee_cents',
        'pj_bulk_subtotal_cents'
      ].some(key => Boolean(attributes[key]));
      if (!currentFingerprint && !removedLegacyFees && !hasManagedAttributes) {
        ready = true;
        return cart;
      }
      if (!ready || state.fingerprint !== currentFingerprint) {
        const agreed = Boolean(currentFingerprint) &&
          COMPATIBLE_VERSIONS.has(attributes.pj_bulk_policy_version) &&
          attributes.pj_bulk_fingerprint === currentFingerprint;
        state = {agreed, fingerprint: currentFingerprint,
          acceptedAt: agreed ? (attributes.pj_bulk_policy_accepted_at || '') : ''};
      }
      if (action?.kind === 'agree') {
        state.agreed = action.value;
        state.acceptedAt = action.value ? new Date().toISOString() : '';
      }
      if (!currentFingerprint) state = {agreed: false, fingerprint: '', acceptedAt: ''};

      await request('cart/update.js', {attributes: {
        pj_bulk_qa: '',
        pj_bulk_policy_version: state.agreed ? VERSION : '',
        pj_bulk_policy_accepted_at: state.agreed ? state.acceptedAt : '',
        pj_bulk_fingerprint: currentFingerprint,
        pj_bulk_delivery_promise_version: currentFingerprint ? DELIVERY_PROMISE_VERSION : '',
        pj_bulk_protection: '',
        pj_bulk_protection_fee_cents: '',
        pj_bulk_subtotal_cents: ''
      }});
      await refresh();
      ready = true;
      return cart;
    } catch (failure) {
      ready = true; error = failure.message || 'Your cart could not be updated. Please try again.';
      throw failure;
    } finally {busy = false; render();}
  }
  function enqueue(action) {
    const next = queue.catch(() => {}).then(() => sync(action));
    queue = next;
    next.catch(() => {});
    return next;
  }
  class BulkCart extends HTMLElement {
    connectedCallback() { queueMicrotask(render); }
    disconnectedCallback() { queueMicrotask(render); }
  }
  customElements.define('pj-bulk-cart', BulkCart);
  document.addEventListener('change', event => {
    if (event.target.matches('[data-bulk-agree]')) enqueue({kind: 'agree', value: event.target.checked});
  });
  async function checkout(event) {
    const button = event.target.closest?.('button[name="checkout"],a[href*="/checkout"]');
    const form = event.type === 'submit' && (event.submitter?.name === 'checkout' || event.target.querySelector('[name="checkout"]'));
    if ((!button && !form) || !panels().length) return;
    event.preventDefault(); event.stopImmediatePropagation();
    if (busy || externalUpdate || !resolutionReady || !state.agreed) return;
    try {
      await enqueue();
      if (state.agreed && !error && panels().length) location.assign(root + 'checkout');
    } catch (_) { panels()[0]?.querySelector('[data-bulk-status]')?.focus(); }
  }
  document.addEventListener('click', checkout, true);
  document.addEventListener('submit', checkout, true);
  document.addEventListener('cart:updating', event => { if (!event.detail?.pjBulkPolicy) { externalUpdate = true; render(); } });
  document.addEventListener('cart:updated', event => {
    if (event.detail?.pjBulkPolicy) return;
    externalUpdate = false;
    enqueue();
  });
  document.addEventListener('pj:bulk-resolution-state', event => {
    resolutionReady = Boolean(event.detail?.ready);
    render();
  });
  window.addEventListener('pageshow', event => { if (event.persisted) enqueue(); });
  const observer = new MutationObserver(records => {
    if (records.some(record => [...record.addedNodes].some(node => node.nodeType === 1 &&
      (node.matches?.('button[name="checkout"],pj-bulk-cart') || node.querySelector?.('button[name="checkout"],pj-bulk-cart'))))) render();
  });
  observer.observe(document.body, {childList: true, subtree: true});
  enqueue();
})();
